import React, { useState } from 'react';
import { BuildingIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { DataTable } from '../../components/ui/DataTable';
import { FeatureGate } from '../../components/platform/FeatureStatusBadge';
import { maintenanceRequests, managedProperties, occupancyTrend, rentLedger, tenancyApplications, tenants } from '../../data/property';
import { naira, percent } from '../../utils/format';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Portfolio', 'Inventory', 'Applications', 'Tenants', 'Rent', 'Maintenance'] as const;
const BED_TONE: Record<string, string> = {
  occupied: 'bg-brand-700',
  vacant: 'bg-line-strong',
  reserved: 'bg-warn-700',
  maintenance: 'bg-danger-700'
};
export function PropertyOverview() {
  const [tab, setTab] = useSectionRoute('/property', TABS);
  const allBeds = managedProperties.flatMap(property => property.buildings.flatMap(building => building.rooms.flatMap(room => room.beds)));
  const occupied = allBeds.filter(bed => bed.status === 'occupied').length;
  const arrears = tenants.reduce((total, tenant) => total + tenant.balance, 0);
  return <div className="space-y-6" data-id="element-8238" data-mp="3vuura978vhu1">
      <PageHeader eyebrow="Cedarwood Living" title="Property console" description="Occupancy, applications, tenants, rent records and maintenance across your three Lagos properties." data-id="element-8239" data-mp="3vtjoocpjcg4i" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Property sections" data-id="element-8240" data-mp="9nwhda052ab3" />

      {tab === 'Portfolio' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-8241" data-mp="dmdqu0717wvo">
          <div className="space-y-6 lg:col-span-2" data-id="element-8242" data-mp="2bfjbhkfbulbs">
            <Panel title="Properties" bodyClassName="p-0" data-id="element-8243" data-mp="2bgudbep7pj09">
              <ul className="divide-y divide-line" data-id="element-8244" data-mp="2fgux7ev2ymep">
                {managedProperties.map(property => {
              const beds = property.buildings.flatMap(building => building.rooms.flatMap(room => room.beds));
              const let_ = beds.filter(bed => bed.status === 'occupied').length;
              const vacant = beds.length - let_;
              return <li key={property.id} className="gap-4 p-4 sm:flex" data-id="element-8245" data-mp="3nsg64ijmo3dn">
                      {/* A landlord recognises the building before the name. */}
                      <img src={property.image} alt={`${property.name}, ${property.address}`} loading="lazy" className="h-36 w-full shrink-0 rounded-xl object-cover sm:h-28 sm:w-44" data-id="element-8246" data-mp="kkv6z5zwr3y" />
                      <div className="mt-3 min-w-0 flex-1 sm:mt-0" data-id="element-8247" data-mp="obqc7hno9pfu">
                        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-8248" data-mp="jcdnerd8q8am">
                          <div className="min-w-0" data-id="element-8249" data-mp="12xx4xsxmy602">
                            <p className="text-body font-semibold text-ink" data-id="element-8250" data-mp="12yglp6bjuoci">{property.name}</p>
                            <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-8251" data-mp="17w8es3nxkgia">{property.address}</p>
                            <p className="cz-num mt-0.5 text-caption text-ink-subtle" data-id="element-8252" data-mp="twft4mr9gqh6">
                              {property.kind} · {property.buildings.length} building(s) ·{' '}
                              {beds.length} beds
                            </p>
                          </div>
                          <Badge tone={property.verification === 'verified' ? 'ok' : 'warn'} data-id="element-8253" data-mp="ozgxu0vv2dhe">
                            {property.verification === 'verified' ? 'Verified' : 'Verification pending'}
                          </Badge>
                        </div>
                        <div className="mt-3 w-full max-w-xs" data-id="element-8254" data-mp="1aeg6t188kmw">
                          <Meter value={percent(let_, beds.length)} label="Occupancy" trailing={`${let_}/${beds.length}`} data-id="element-8255" data-mp="6639iemnl5ar" />
                        </div>
                        <p className="mt-2 text-caption text-ink-muted" data-id="element-8256" data-mp="3km1khkab4m31">
                          {vacant === 0 ? 'Fully let — nothing to advertise here.' : `${vacant} ${vacant === 1 ? 'bed is' : 'beds are'} earning nothing today.`}
                        </p>
                      </div>
                    </li>;
            })}
              </ul>
            </Panel>

            <Panel title="Occupancy trend" description="Share of beds let, last six months" data-id="element-8257" data-mp="31o74gmarhab5">
              <div className="flex items-end gap-3" data-id="element-8258" data-mp="2xn5aajvpbog1">
                {occupancyTrend.map(point => <div key={point.month} className="flex flex-1 flex-col items-center gap-2" data-id="element-8259" data-mp="zviu7szp9mlv">
                    <div className="w-full rounded-t bg-brand-700" style={{
                height: `${point.occupancy * 1.4}px`
              }} data-id="element-8260" data-mp="vvs7q1h7hsn5" />
                    <span className="cz-num text-[12px] text-ink-muted" data-id="element-8261" data-mp="2iqdoo70svrbr">{point.month}</span>
                    <span className="cz-num text-[12px] font-medium text-ink" data-id="element-8262" data-mp="2nhn82pvr9yf7">
                      {point.occupancy}%
                    </span>
                  </div>)}
              </div>
            </Panel>
          </div>

          <div className="space-y-6" data-id="element-8263" data-mp="1uv2gbobdbkb4">
            <Panel title="Right now" bodyClassName="p-0" data-id="element-8264" data-mp="1zs19o4n7jn89">
              <dl className="divide-y divide-line" data-id="element-8265" data-mp="1zsksroolghnv">
                {[{
              label: 'Beds let',
              value: `${occupied} of ${allBeds.length}`
            }, {
              label: 'Vacancies',
              value: String(allBeds.length - occupied)
            }, {
              label: 'New applications',
              value: String(tenancyApplications.filter(a => a.status === 'New').length)
            }, {
              label: 'Rent in arrears',
              value: naira(arrears)
            }, {
              label: 'Open maintenance',
              value: String(maintenanceRequests.filter(request => request.status !== 'Closed').length)
            }].map(row => <div key={row.label} className="flex justify-between gap-3 px-5 py-3" data-id="element-8266" data-mp="1p6azc37lx6ul">
                    <dt className="text-[13px] text-ink-muted" data-id="element-8267" data-mp="11ggn9nv43mbe">{row.label}</dt>
                    <dd className="cz-num text-[13px] font-semibold text-ink" data-id="element-8268" data-mp="wgu99c7gm21j">{row.value}</dd>
                  </div>)}
              </dl>
            </Panel>
            <Panel title="Payments" data-id="element-8269" data-mp="1b71kp7z1uksq">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-8270" data-mp="16e8bo7wjsqau">
                Rent is collected through HavaPay. Campuzon holds the tenancy and payment record;
                money movement stays with the regulated provider.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Inventory' && <div className="space-y-6" data-id="element-8271" data-mp="2pbv8x5zt54fx">
          {managedProperties.map(property => <Panel key={property.id} title={property.name} description="Property → building → floor → room → bed" bodyClassName="p-0" data-id="element-8272" data-mp="2u5hao34fnnwy">
              {property.buildings.map(building => <div key={building.id} className="border-b border-line px-5 py-4 last:border-b-0" data-id="element-8273" data-mp="3835xhgi2954n">
                  <p className="flex items-center gap-2 text-[13px] font-semibold text-ink" data-id="element-8274" data-mp="3d3ad74b59vtt">
                    <BuildingIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-8275" data-mp="2z3isylu1bj7d" />
                    {building.name}
                  </p>
                  <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-8276" data-mp="340rbinj3hp95">
                    {building.rooms.map(room => <li key={room.id} className="rounded-lg border border-line px-3 py-2.5" data-id="element-8277" data-mp="1nq6xozccc8wt">
                        <p className="cz-num text-[13px] font-medium text-ink" data-id="element-8278" data-mp="1skueec13rz0u">{room.label}</p>
                        <p className="text-[12px] text-ink-muted" data-id="element-8279" data-mp="2jaw9a3b96k4y">
                          {room.floor} floor · {room.roomType}
                        </p>
                        <ul className="mt-2 space-y-1.5" data-id="element-8280" data-mp="2addhmg33t7k3">
                          {room.beds.map(bed => <li key={bed.id} className="flex items-center gap-2 text-[12px]" data-id="element-8281" data-mp="25eizlgcynn11">
                              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${BED_TONE[bed.status]}`} data-id="element-8282" data-mp="32zojrv6czgj0" />
                              <span className="text-ink-muted" data-id="element-8283" data-mp="2t9lt41mpmenu">{bed.label}</span>
                              <span className="ml-auto text-ink-subtle" data-id="element-8284" data-mp="2ob27618492vh">
                                {bed.tenantName ?? bed.status}
                              </span>
                            </li>)}
                        </ul>
                      </li>)}
                  </ul>
                </div>)}
            </Panel>)}
        </div>}

      {tab === 'Applications' && <Panel title="Applications" bodyClassName="p-0" data-id="element-8285" data-mp="24p7z1eo9qpx">
          <DataTable caption="Tenancy applications awaiting a decision" rowKey={application => application.id} rows={tenancyApplications} columns={[{
        header: 'Applicant',
        primary: true,
        cell: a => a.applicant
      }, {
        header: 'Programme',
        cell: a => a.programme
      }, {
        header: 'Property',
        cell: a => a.property
      }, {
        header: 'Room',
        cell: a => a.roomPreference
      }, {
        header: 'Move-in',
        cell: a => <span className="cz-num" data-id="element-8287" data-mp="utcz3dm3rabe">{a.moveIn}</span>
      }, {
        header: 'Status',
        status: true,
        cell: a => <Badge tone={a.status === 'Approved' ? 'ok' : a.status === 'Declined' ? 'danger' : a.status === 'Screening' ? 'brand' : 'warn'} data-id="element-8288" data-mp="yslne2ydifzk">
                    {a.status}
                  </Badge>
      }]} data-id="element-8286" data-mp="234cn9kja3r0" />
        </Panel>}

      {tab === 'Tenants' && <Panel title="Tenants" bodyClassName="p-0" data-id="element-8289" data-mp="3umkmmfess89h">
          <DataTable caption="Current tenants and their standing" rowKey={tenant => tenant.id} rows={tenants} columns={[{
        header: 'Tenant',
        primary: true,
        cell: t => t.name
      }, {
        header: 'Unit',
        cell: t => <span className="cz-num" data-id="element-8291" data-mp="2a78xv14wk1f3">
                    {t.property} · {t.unit}
                  </span>
      }, {
        header: 'Lease',
        cell: t => <span className="cz-num" data-id="element-8292" data-mp="1xw87np31lbd3">
                    {t.leaseStart} — {t.leaseEnd}
                  </span>
      }, {
        header: 'Rent',
        align: 'right',
        cell: t => <span className="cz-num" data-id="element-8293" data-mp="2ln3u06oqbecy">{naira(t.rentPerYear)}</span>
      }, {
        header: 'Balance',
        align: 'right',
        cell: t => <span className="cz-num font-medium" data-id="element-8294" data-mp="2boxtntikmga7">
                    {t.balance === 0 ? '—' : naira(t.balance)}
                  </span>
      }, {
        header: 'Status',
        status: true,
        cell: t => <Badge tone={t.status === 'Overdue' ? 'danger' : t.status === 'Ending soon' ? 'warn' : 'ok'} data-id="element-8295" data-mp="vaxyswyl1gtt">
                    {t.status}
                  </Badge>
      }]} data-id="element-8290" data-mp="3hku0we6h3fl" />
        </Panel>}

      {tab === 'Rent' && <div className="space-y-5" data-id="element-8296" data-mp="39igykft0ksij">
          <FeatureGate featureKey="rent_payment" data-id="element-8297" data-mp="3ehtnd63g49nr" />
        <Panel title="Rent ledger" description="A record of what is owed and what has been received. Campuzon does not collect it." bodyClassName="p-0" data-id="element-8298" data-mp="3egijz7dwx3x6">
          <ul className="divide-y divide-line" data-id="element-8299" data-mp="34kfh1zb8l196">
            {rentLedger.map(record => <li key={record.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-8300" data-mp="2qtab6fh9zcqe">
                <div className="min-w-0 flex-1" data-id="element-8301" data-mp="2vsdi65gtft10">
                  <p className="text-[13px] font-medium text-ink" data-id="element-8302" data-mp="2voy0cuvrc5vw">{record.tenant}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-8303" data-mp="2gutf543hc7xh">
                    {record.unit} · {record.period} · due {record.due} · {record.method}
                  </p>
                </div>
                <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-8304" data-mp="229fdymjwmaat">
                  {naira(record.amount)}
                </span>
                <Badge tone={record.status === 'Paid' ? 'ok' : record.status === 'Overdue' ? 'danger' : 'warn'} data-id="element-8305" data-mp="2c61yfa8bizcb">
                  {record.status}
                </Badge>
              </li>)}
          </ul>
        </Panel>
        </div>}

      {tab === 'Maintenance' && <Panel title="Maintenance requests" bodyClassName="p-0" data-id="element-8306" data-mp="2uy4dih5ba7kg">
          <ul className="divide-y divide-line" data-id="element-8307" data-mp="2py89wj0j0i5f">
            {maintenanceRequests.map(request => <li key={request.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-8308" data-mp="34uzagvlxrzwm">
                <div className="min-w-0 flex-1" data-id="element-8309" data-mp="2zweamxalji8p">
                  <p className="cz-num text-[13px] font-medium text-ink" data-id="element-8310" data-mp="x8fq4qpsaesj">
                    {request.reference} · {request.category}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-8311" data-mp="125y1sov6r848">
                    {request.property} · {request.unit} · raised {request.raised} ·{' '}
                    {request.assignee}
                  </p>
                </div>
                <Badge tone={request.priority === 'Urgent' ? 'danger' : 'neutral'} data-id="element-8312" data-mp="e9lcxm5yxmeh">
                  {request.priority}
                </Badge>
                <Badge tone={request.status === 'Closed' ? 'ok' : 'brand'} data-id="element-8313" data-mp="j9pwxm634z9g">{request.status}</Badge>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}